import { IFragmentCta, IFragmentCtaFields } from 'types/contentful';

export function env(key: string): string {
  return (process || {}).env?.[key] || 'NA';
}

export function formatPhoneNumber(phone: string): string {
  const areaCode = phone.slice(0, 3);
  const exchange = phone.slice(3, 6);
  const line = phone.slice(6, 10);

  return `(${areaCode}) ${exchange}-${line}`;
}

/**
 * format used only for structured data
 */
export function structuredPhoneNumber(phone: string): string {
  const areaCode = phone.slice(0, 3);
  const exchange = phone.slice(3, 6);
  const line = phone.slice(6, 10);

  return `+1-${areaCode}-${exchange}-${line}`;
}

export const doctorDisplayName = (doctor: {
  firstName: string;
  lastName: string;
  titles: string[];
}): string => `${doctor.firstName} ${doctor.lastName}, ${doctor.titles.join(', ')}`;

export const pluralize = (name: string): string => `${name}${name.slice(-1) === 's' ? "'" : "'s"}`;

const NUM_TO_STRING = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

export const numberToString = (number: number): string | number => {
  if (number > 10) return number;
  return NUM_TO_STRING[number];
};

export function possess(pluralNoun: string): string {
  return pluralNoun.replace(/s$/, "'s");
}

export const buildSrcSet = (
  url: string,
  imageWidth: number,
  size: number,
  webP = false
): string => {
  let more = '';
  if (size > 1) {
    more = `${buildSrcSet(url, imageWidth, size - 1, webP)}, `;
  }
  return `${more}${url}?w=${imageWidth * size}${webP ? '&fm=webp' : ''} ${size}x `;
};

export const convertTime12to24 = (initialTime: string): string => {
  if (!initialTime) {
    return 'Closed';
  }

  const [time, modifier] = initialTime.split(' ');
  const parts = time.split(':');
  let hours: string | number = parts[0];
  const minutes = parts[1];

  if (hours === '12') {
    hours = '00';
  }

  if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }

  return `${hours < 10 ? `0${hours}` : hours}:${minutes}:00`;
};

export const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&#039;');
};

export const getItemFromLocalStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

export const addItemToLocalStorage = (key: string, item: string): void => {
  try {
    window.localStorage.setItem(key, item);
  } catch (error) {
    console.log('localStorage not supported');
  }
};

export const removeItemFromLocalStorage = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.log('localStorage not supported');
  }
};

/**
 * Removes any field where the value is undefined.  This is useful to avoid
 * serialization errors with React props.  All React props must be serializable
 * to JSON, and JSON doesn't support undefined, so usually you want to just remove
 * the property entirely.
 */
export function removeIfUndefined<V, O extends Record<string, V | undefined>>(
  test: O
): Record<string, V> {
  return Object.entries(test).reduce<Record<string, V>>((acc, [key, val]) => {
    if (val) {
      acc[key] = val;
    }
    return acc;
  }, {});
}

/** A mapping of type URL that removes functions and readonly
 * properties while making remaining properties optional */
export type UrlObject = Omit<
  { [Property in keyof URL]+?: URL[Property] },
  'origin' | 'toString' | 'searchParams' | 'toJSON'
>;

/** a replacement for url.format since it is deprecated in Node.JS and unavailable in browser */
export function urlFrom(urlObject: UrlObject): string {
  return String(Object.assign(new URL('http://a.org'), urlObject));
}

/** Removes all duplicate values using a hashed value of each element to test for same-value-zero equality */
export function unique<T, H extends string | number>(elems: T[], hash: (elem: T) => H): T[] {
  return elems.reduce<{ found: Set<H>; acc: T[] }>(
    ({ found, acc }, elem) => {
      const h = hash(elem);
      if (found.has(h)) {
        return { found, acc };
      } else {
        found.add(h);
        acc.push(elem);

        return { found, acc };
      }
    },
    { found: new Set<H>(), acc: [] }
  ).acc;
}

/**
 * Get distance between two lat/lng coords.
 *
 * @param lat1
 * @param lng1
 * @param lat2
 * @param lng2
 * @param unit
 * @returns
 */
export const getDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  unit?: string
): number => {
  if (lat1 === lat2 && lng1 === lng2) {
    return 0;
  }

  const radlat1 = (Math.PI * lat1) / 180;
  const radlat2 = (Math.PI * lat2) / 180;
  const theta = lng1 - lng2;
  const radtheta = (Math.PI * theta) / 180;
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  if (unit == 'K') {
    dist = dist * 1.609344;
  }
  if (unit == 'N') {
    dist = dist * 0.8684;
  }
  return dist;
};

export const joinString = (arr: string[], separator = ' '): string =>
  arr.filter((s) => s && s.length).join(separator);

export const isImageContentType = (contentType: string): boolean => {
  return contentType.startsWith('image/');
};

export const formatCityName = (city: string): string => {
  return city
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const CITY_NAME_ABBREVIATIONS: Record<string, string> = {
  st: 'St.',
  mt: 'Mt.',
  ft: 'Ft.',
  lt: 'Lt.',
  pt: 'Pt.',
};

/** Formats a URL city slug for CMS city fields that use abbreviated periods (e.g. St. Louis). */
export const formatCityNameForCms = (city: string): string => {
  const normalized = city.includes('-') ? formatCityName(city) : city;

  return normalized
    .split(' ')
    .map((word) => {
      const abbreviationKey = word.replace(/\.$/, '').toLowerCase();
      if (word.endsWith('.') || !CITY_NAME_ABBREVIATIONS[abbreviationKey]) {
        return word;
      }
      return CITY_NAME_ABBREVIATIONS[abbreviationKey];
    })
    .join(' ');
};

/** Returns city display names to match against CMS, including common abbreviation variants. */
export const cityNamesForCmsQuery = (city: string): string[] => {
  const formatted = formatCityName(city);
  const withAbbreviations = formatCityNameForCms(city);
  return formatted === withAbbreviations ? [formatted] : [formatted, withAbbreviations];
};

export const textToSlug = (text: string): string => {
  return text
    .normalize('NFD') // separate accent from letter
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // remove special chars
    .replace(/\s+/g, '-') // spaces to dash
    .replace(/-+/g, '-'); // collapse multiple dashes
};

export const formatStateCode = (state: string): string => {
  return (state ?? '').toUpperCase();
};

const STATES = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'American Samoa', value: 'AS' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'District of Colombia', value: 'DC' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Federated States of Micronesia', value: 'FM' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Guam', value: 'GU' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Maine', value: 'ME' },
  { label: 'Marshall Islands', value: 'MH' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Northern Mariana Islands', value: 'MP' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Montana', value: 'MT' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New York', value: 'NY' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Puerto Rico', value: 'PR' },
  { label: 'Palau', value: 'PW' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'St. John', value: 'SJ' },
  { label: 'St. Thomas', value: 'ST' },
  { label: 'St. Croix', value: 'SX' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Virgin Islands', value: 'VI' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Washington', value: 'WA' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wyoming', value: 'WY' },
];

export const getStateFromCode = (code: string): string => {
  return STATES.find((state) => state.value === formatStateCode(code))?.label ?? '';
};

export const getStateCodeFromNameSlug = (nameSlug: string): string => {
  return STATES.find((state) => textToSlug(state.label) === nameSlug)?.value?.toLowerCase() ?? '';
};

export const getDoctorHubParams = (
  query: Record<string, string | string[] | undefined>
): { state: string; stateCode: string; city: string; hub: string; doctorType: string } => {
  const city = (query['city'] || '') as string;
  let stateCode = (query['state'] || '') as string;
  let hub = (city ? city : stateCode) as string;
  let doctorType = '';
  let state = '';

  if (!stateCode && !city) {
    const slugArray: string | string[] = query['slug'] || [];
    if (
      Array.isArray(slugArray) &&
      slugArray.length === 2 &&
      ['optometrists', 'ophthalmologists'].includes(String(slugArray[0]).toLowerCase())
    ) {
      state = String(slugArray[1]);
      stateCode = getStateCodeFromNameSlug(state);
      hub = state;
      const typeSlug = String(slugArray[0]).toLowerCase();
      doctorType = typeSlug === 'optometrists' ? 'Optometrist' : 'Ophthalmologist';
    }
  }

  return { state, stateCode, city, hub, doctorType };
};

/** Wraps inline CTA fields in a Contentful entry shape for components that expect IFragmentCta. */
export function asFragmentCta(fields: IFragmentCtaFields): IFragmentCta {
  return {
    fields,
    sys: {
      id: fields.title,
      type: 'Entry',
      createdAt: '',
      updatedAt: '',
      locale: 'en-US',
      contentType: {
        sys: {
          id: 'fragmentCta',
          linkType: 'ContentType',
          type: 'Link',
        },
      },
    },
  } as IFragmentCta;
}
